import React, { useState } from "react";
import {
  Plus,
  Folder,
  FolderPlus,
  Users,
  Database,
  Search,
  ChevronDown,
  FileText,
  Settings,
  Filter
} from "lucide-react";
import { Link } from "react-router-dom";

const Assemblies = () => {
  const [activeTab, setActiveTab] = useState("Properties");

  return (
    <div className="flex h-screen bg-gradient-to-br from-[#0d1b2a] to-[#1a2d45] text-white">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0d1b2a]/80 backdrop-blur-lg border-r border-[#2d3e56] flex flex-col">
        {/* Logo */}
        <div className="flex items-center px-6 py-6 border-b border-[#2d3e56]">
          <img
            src="/assets/images/rlogo.png"
            alt="Redian Logo"
            className="h-12 w-100 object-contain mr-3"
          />
          {/* <span className="font-bold text-xl tracking-tight">Redian</span> */}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-4 py-6 space-y-1">
          <Link
            to="/create"
            className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 py-3 rounded-lg mb-6 transition-all duration-200 shadow-lg shadow-blue-600/20"
          >
            <Plus size={18} />
            <span className="font-medium">Create Project</span>
          </Link>
          <Link
            to="/home"
            className="flex items-center space-x-3 px-3 py-3 rounded-lg hover:bg-[#2d3e56] cursor-pointer group transition-colors"
          >
            <Folder size={18} className="text-gray-400 group-hover:text-white" />
            <span className="text-gray-300 group-hover:text-white">Projects</span>
          </Link>

          <Link
            to="/templates"
            className="flex items-center space-x-3 px-3 py-3 rounded-lg hover:bg-[#2d3e56] cursor-pointer group transition-colors"
          >
            <FolderPlus size={18} className="text-gray-400 group-hover:text-white" />
            <span className="text-gray-300 group-hover:text-white">Templates</span>
          </Link>

          <Link
            to="/assemblies"
            className="flex items-center space-x-3 px-3 py-3 rounded-lg bg-blue-600/20 border border-blue-500/30 cursor-pointer group transition-colors"
          >
            <Database size={18} className="text-blue-400" />
            <span className="font-medium">Assemblies</span>
          </Link>
        </nav>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-[#2d3e56]">
          <div className="text-sm text-gray-400 font-medium mb-2">
            Demo Projects
          </div>
          <div className="space-y-2">
            <div className="text-xs text-gray-500 hover:text-gray-300 cursor-pointer py-1 transition-colors">
              Architecture Concept
            </div>
            <div className="text-xs text-gray-500 hover:text-gray-300 cursor-pointer py-1 transition-colors">
              Urban Planning
            </div>
            <div className="text-xs text-gray-500 hover:text-gray-300 cursor-pointer py-1 transition-colors">
              Landscape Design
            </div>
          </div>
          {/* Logout Button */}
          <div className="mt-6">
            <Link
              to="/"
              className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-400 border border-red-500/40 rounded-lg hover:bg-red-500/10 hover:text-red-300 transition-colors"
            >
              Logout
            </Link>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex  items-center justify-between px-8 py-4 border-b border-[#2d3e56] bg-[#0d1b2a]/50 backdrop-blur-sm">
          <div className="flex-1 h-16 max-w-2xl relative">
            <Search size={18} className="absolute left-3 top-6 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search properties, items, or assemblies..."
              className="w-full pl-10 pr-4 py-3 bg-[#1b263b] border border-[#2d3e56] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
            />
          </div>
          <div className="flex items-center space-x-4 ml-6">
            <button className="text-gray-400 hover:text-white text-sm transition-colors flex items-center space-x-2">
              <FileText size={16} />
              <span>How to create a property</span>
            </button>
            <button className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-600/20">
              <Plus size={16} />
              <span className="font-medium">New Property</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="h-9 w-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center font-semibold shadow-lg">
                Admin
              </div>
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex border-b border-[#2d3e56] px-8 bg-[#0d1b2a]/30">
          {["Properties", "Items", "Assemblies"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-all duration-200 ${
                activeTab === tab
                  ? "text-white border-blue-500 bg-blue-500/10"
                  : "text-gray-400 border-transparent hover:text-white hover:bg-[#1b263b]/50"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Table Section */}
        <div className="flex-1 overflow-auto p-8">
          {activeTab === "Properties" && (
            <div className="bg-[#1b263b]/50 backdrop-blur-sm rounded-2xl border border-[#2d3e56] overflow-hidden">
              {/* Table Header */}
              <div className="flex items-center justify-between p-6 border-b border-[#2d3e56]">
                <div>
                  <h3 className="text-lg font-semibold mb-1">Property Definitions</h3>
                  <p className="text-gray-400 text-sm">Manage and organize your property definitions</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button className="flex items-center space-x-2 px-3 py-2 bg-[#0d1b2a] border border-[#2d3e56] rounded-lg hover:border-blue-500/50 transition-colors">
                    <Filter size={16} />
                    <span>Filter</span>
                  </button>
                  <button className="flex items-center space-x-2 px-3 py-2 bg-[#0d1b2a] border border-[#2d3e56] rounded-lg hover:border-blue-500/50 transition-colors">
                    <Settings size={16} />
                    <span>Settings</span>
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#0d1b2a] text-gray-400">
                    <tr>
                      <th className="px-6 py-4 text-left font-medium">Properties</th>
                      <th className="px-6 py-4 text-left font-medium">Type</th>
                      <th className="px-6 py-4 text-left font-medium">Value</th>
                      <th className="px-6 py-4 text-left font-medium">Unit</th>
                      <th className="px-6 py-4 text-left font-medium">Created</th>
                      <th className="px-6 py-4 text-left font-medium">Last Modified</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2d3e56]">
                    <tr className="hover:bg-[#0d1b2a]/50 cursor-pointer transition-colors group">
                      <td className="px-6 py-4 font-medium text-white group-hover:text-blue-400 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span>Basic Elements (Wall, Floor, Column, etc)</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                    </tr>
                    <tr className="hover:bg-[#0d1b2a]/50 cursor-pointer transition-colors group">
                      <td className="px-6 py-4 font-medium text-white group-hover:text-blue-400 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span>CF properties</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                    </tr>
                    <tr className="hover:bg-[#0d1b2a]/50 cursor-pointer transition-colors group">
                      <td className="px-6 py-4 font-medium text-white group-hover:text-blue-400 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          <span>Geometry</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                      <td className="px-6 py-4 text-gray-300">–</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab !== "Properties" && (
            <div className="flex flex-col items-center justify-center h-full text-center py-20">
              <div className="max-w-md mx-auto">
                <div className="inline-flex p-6 bg-blue-500/10 rounded-2xl mb-6">
                  <Database size={48} className="text-blue-400" />
                </div>
                <h3 className="text-2xl font-bold mb-2">No {activeTab.toLowerCase()} found</h3>
                <p className="text-gray-400 mb-6">This section is currently empty. Start by creating new {activeTab.toLowerCase()}.</p>
                <button className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-600/20">
                  <Plus size={18} />
                  <span className="font-medium">Create {activeTab.slice(0, -1)}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Assemblies;
